import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Share2, Users, ChevronLeft, Network } from 'lucide-react';
import api from '@/lib/api';
import BottomNav from '@/components/BottomNav';
import ReferralShareModal from '@/components/referral/ReferralShareModal';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import ResqlyEmptyState from '@/components/resqly/ResqlyEmptyState';
import { Button } from '@/components/ui/button';
import {
  buildReferralLink,
  copyText,
  formatReferralStatus,
} from '@/lib/referral';

export default function ConsumerReferral() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [me, setMe] = useState(null);
  const [direct, setDirect] = useState([]);
  const [activity, setActivity] = useState([]);
  const [network, setNetwork] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [meRes, directRes, activityRes, networkRes] = await Promise.all([
        api.get('/referrals/me'),
        api.get('/referrals/me/direct'),
        api.get('/referrals/me/activity'),
        api.get('/referrals/me/network'),
      ]);
      setMe(meRes.data);
      setDirect(directRes.data.direct_referrals || []);
      setActivity(activityRes.data.activity || []);
      setNetwork(networkRes.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load referral dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const code = me?.referral_code || '';
  const link = buildReferralLink(code);
  const stats = me?.stats || {};

  const onCopyCode = async () => {
    await copyText(code);
    toast.success('Referral code copied');
  };

  const onCopyLink = async () => {
    await copyText(link);
    toast.success('Referral link copied');
  };

  const onShare = () => setShareOpen(true);

  return (
    <div className="resqly-shell pb-24">
      <div className="resqly-frame">
        <header className="px-5 pt-5 pb-3 flex items-center gap-2">
          <button type="button" onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full hover:bg-slate-100">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold text-slate-900">Refer & Earn</h1>
        </header>

        <div className="px-5 space-y-4">
          {loading && <p className="text-sm text-slate-500">Loading referral dashboard…</p>}
          {error && (
            <ResqlyCard className="p-4">
              <p className="text-sm text-rose-600">{error}</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={load}>Retry</Button>
            </ResqlyCard>
          )}

          {!loading && !error && me && (
            <>
              <ResqlyCard className="p-5 space-y-3" elevated data-testid="consumer-referral-dashboard">
                <p className="text-sm text-slate-600">Your referral code</p>
                <p className="text-2xl font-semibold tracking-wide text-[var(--resqly-primary)]">{code}</p>
                <p className="text-xs text-slate-500">{me.financial_rewards_status}</p>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={onCopyCode} data-testid="copy-referral-code">
                    <Copy className="w-4 h-4 mr-1" /> Copy Code
                  </Button>
                  <Button size="sm" onClick={onShare} data-testid="share-referral">
                    <Share2 className="w-4 h-4 mr-1" /> Share RESQLY
                  </Button>
                  <Button size="sm" variant="ghost" onClick={onCopyLink}>Copy Link</Button>
                </div>
              </ResqlyCard>

              <div className="grid grid-cols-3 gap-2">
                <Stat label="People" value={stats.direct_count || 0} />
                <Stat label="Active" value={stats.active_count || 0} />
                <Stat label="Network" value={stats.total_network || 0} />
              </div>

              <section>
                <h2 className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-1">
                  <Users className="w-4 h-4" /> Your referrals
                </h2>
                {direct.length === 0 ? (
                  <ResqlyEmptyState title="No referrals yet" description="Share your code to grow your RESQLY community." />
                ) : (
                  <div className="space-y-2">
                    {direct.map((row) => (
                      <ResqlyCard key={row.relationship_id} className="p-3 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-slate-900">{row.display_name}</p>
                          <p className="text-xs text-slate-500">{row.role}</p>
                        </div>
                        <span className="text-xs font-medium text-slate-600">
                          {formatReferralStatus(row.status)}
                        </span>
                      </ResqlyCard>
                    ))}
                  </div>
                )}
              </section>

              <section data-testid="consumer-referral-network">
                <h2 className="text-sm font-medium text-slate-800 mb-2 flex items-center gap-1">
                  <Network className="w-4 h-4" /> Your network
                </h2>
                {!network?.network?.length ? (
                  <p className="text-sm text-slate-500">Your extended community will appear here.</p>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-2">Up to {network.levels} levels</p>
                    <NetworkTree nodes={network.network} />
                  </>
                )}
              </section>

              <section>
                <h2 className="text-sm font-medium text-slate-800 mb-2">Referral activity</h2>
                {activity.length === 0 ? (
                  <p className="text-sm text-slate-500">No activity yet.</p>
                ) : (
                  <div className="space-y-2">
                    {activity.slice(0, 10).map((ev) => (
                      <ResqlyCard key={ev.id} className="p-3">
                        <p className="text-sm text-slate-800">{ev.event_type?.replace(/_/g, ' ')}</p>
                        <p className="text-xs text-slate-500">{formatReferralStatus(ev.status)} · {ev.timestamp}</p>
                      </ResqlyCard>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
      <ReferralShareModal
        open={shareOpen}
        onOpenChange={setShareOpen}
        code={code}
      />
      <BottomNav />
    </div>
  );
}

function NetworkTree({ nodes, depth = 0 }) {
  if (!nodes?.length) return null;
  return (
    <ul className={`space-y-2 ${depth > 0 ? 'ml-4 border-l border-slate-200 pl-3' : ''}`}>
      {nodes.map((node) => (
        <li key={node.relationship_id || node.user_id}>
          <ResqlyCard className="p-2 text-sm flex justify-between">
            <span>{node.display_name}</span>
            <span className="text-xs text-slate-500">{formatReferralStatus(node.status)}</span>
          </ResqlyCard>
          <NetworkTree nodes={node.children} depth={depth + 1} />
        </li>
      ))}
    </ul>
  );
}

function Stat({ label, value }) {
  return (
    <ResqlyCard className="p-3 text-center">
      <p className="text-lg font-semibold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </ResqlyCard>
  );
}
